import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Edit, Trash2, History, Loader2, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { IconButtonWithTooltip } from "@/components/ui/icon-button-tooltip";
import { toast } from "@/hooks/use-toast";
import LoaderComponent from "@/components/common/LoaderComponent";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useClinicUserGroups, ClinicUserGroupListItem } from "@/hooks/clinic-user-groups/useClinicUserGroups";
import { useClinicUserGroup } from "@/hooks/clinic-user-groups/useClinicUserGroup";
import { useClinicUserGroupAuditLog } from "@/hooks/clinic-user-groups/useClinicUserGroupAuditLog";
import { OperationsHistoryModal } from "@/v2/components/common/OperationsHistoryModal";

export const ClinicUserGroupsTable = () => {
  const navigate = useNavigate();
  const { deleteClinicUserGroup } = useClinicUserGroup();
  const {
    clinicUserGroups,
    loading,
    currentPage,
    totalPages,
    totalCount,
    pageSize,
    searchValue,
    setCurrentPage,
    setPageSize,
    setSearchValue,
    refetch,
  } = useClinicUserGroups();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ClinicUserGroupListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedGroupForHistory, setSelectedGroupForHistory] = useState<number | null>(null);

  const { auditLogs: clinicUserGroupAuditLogs, loading: auditLogsLoading } = useClinicUserGroupAuditLog(selectedGroupForHistory);

  const handleEdit = (item: ClinicUserGroupListItem) => {
    navigate(`/admin/clinic-user-groups/edit/${item.clinicUserGroupId}`, { state: { clinicUserGroup: item } });
  };

  const handleDeleteClick = (item: ClinicUserGroupListItem) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    console.log('[ClinicUserGroupsTable] Delete confirm clicked for:', itemToDelete.clinicUserGroupId, itemToDelete.groupName);
    setIsDeleting(true);
    try {
      await deleteClinicUserGroup(itemToDelete.clinicUserGroupId, itemToDelete.groupName);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      refetch();
    } catch (error) {
      console.error('[ClinicUserGroupsTable] Delete error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleHistory = (item: ClinicUserGroupListItem) => {
    setSelectedGroupForHistory(item.clinicUserGroupId);
    setIsHistoryModalOpen(true);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const displayPage = currentPage + 1;
  const displayTotalPages = Math.max(1, totalPages);
  const startIndex = totalCount > 0 ? currentPage * pageSize + 1 : 0;
  const endIndex = Math.min((currentPage + 1) * pageSize, totalCount);

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisible = 3;

    if (displayTotalPages <= maxVisible) {
      for (let i = 1; i <= displayTotalPages; i++) pages.push(i);
    } else {
      if (displayPage <= 2) {
        pages.push(1, 2, 3);
      } else if (displayPage >= displayTotalPages - 1) {
        pages.push(displayTotalPages - 2, displayTotalPages - 1, displayTotalPages);
      } else {
        pages.push(displayPage - 1, displayPage, displayPage + 1);
      }
    }
    return pages;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Input
            className="w-64"
            placeholder="Search by Group Name..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          {searchValue && (
            <Button variant="outline" onClick={() => setSearchValue("")} disabled={loading}>
              Clear
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Show</span>
          <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(Number(value))}>
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">Entries</span>
        </div>
      </div>

      <div id="table-admin-clinic-user-groups-container" className="rounded-md border">
        <Table id="table-admin-clinic-user-groups">
          <TableHeader>
            <TableRow id="table-admin-clinic-user-groups-header-row">
              <TableHead id="header-cell-group-name">Group Name</TableHead>
              <TableHead id="header-cell-clinics">Clinics</TableHead>
              <TableHead id="header-cell-user-count">Users</TableHead>
              <TableHead id="header-cell-created-date">Created Date</TableHead>
              <TableHead id="header-cell-actions" className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody id="table-admin-clinic-user-groups-body">
            {loading ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <LoaderComponent size="md" text="Loading clinic user groups..." />
                </TableCell>
              </TableRow>
            ) : clinicUserGroups.length === 0 ? (
              <TableRow id="row-clinic-user-group-empty">
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No clinic user group records found
                </TableCell>
              </TableRow>
            ) : (
              clinicUserGroups.map((item) => (
                <TableRow key={item.clinicUserGroupId} id={`row-clinic-user-group-${item.clinicUserGroupId}`}>
                  <TableCell id={`cell-cug-${item.clinicUserGroupId}-name`} className="font-medium">
                    {item.groupName}
                  </TableCell>
                  <TableCell id={`cell-cug-${item.clinicUserGroupId}-clinics`}>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {item.clinics && item.clinics.length > 0 ? (
                        <>
                          {item.clinics.slice(0, 2).map((clinic) => (
                            <Badge
                              key={clinic.clinicId}
                              variant="secondary"
                              className="text-xs font-normal whitespace-nowrap"
                            >
                              {clinic.clinicName}
                            </Badge>
                          ))}
                          {item.clinics.length > 2 && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-auto px-2 py-0.5 text-xs text-primary hover:text-primary/80 font-medium"
                                >
                                  +{item.clinics.length - 2} more
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-64 p-0" align="start">
                                <div className="px-3 py-2 border-b bg-muted/50">
                                  <p className="text-sm font-medium">
                                    Clinics ({item.clinics.length})
                                  </p>
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                  <ul className="divide-y">
                                    {item.clinics.map((clinic) => (
                                      <li key={clinic.clinicId} className="px-3 py-2 text-sm text-foreground">
                                        {clinic.clinicName}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell id={`cell-cug-${item.clinicUserGroupId}-users`}>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto px-2 py-1 gap-1.5 text-primary hover:text-primary/80 font-medium"
                        >
                          <Users className="h-3.5 w-3.5" />
                          {item.totalUserCount}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-0" align="start">
                        <div className="px-3 py-2 border-b bg-muted/50">
                          <p className="text-sm font-medium">
                            Users ({item.totalUserCount})
                          </p>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {item.users && item.users.length > 0 ? (
                            <ul className="divide-y">
                              {item.users.map((u) => (
                                <li key={u.userId} className="px-3 py-2 text-sm">
                                  <span className="text-foreground">{u.appUserName || u.userName}</span>
                                  {u.appUserName && (
                                    <span className="block text-xs text-muted-foreground">{u.userName}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="px-3 py-4 text-sm text-muted-foreground text-center">No users</p>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                  <TableCell id={`cell-cug-${item.clinicUserGroupId}-date`}>
                    {formatDate(item.createdAt)}
                  </TableCell>
                  <TableCell id={`cell-cug-${item.clinicUserGroupId}-actions`} className="text-right">
                    <div className="flex justify-end gap-2">
                      <IconButtonWithTooltip
                        id={`btn-cug-${item.clinicUserGroupId}-history`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleHistory(item)}
                        tooltip="View History"
                        icon={<History className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-cug-${item.clinicUserGroupId}-edit`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(item)}
                        tooltip="Edit"
                        icon={<Edit className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-cug-${item.clinicUserGroupId}-delete`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteClick(item)}
                        className="text-destructive hover:text-destructive"
                        disabled={isDeleting}
                        tooltip="Delete"
                        icon={<Trash2 className="h-4 w-4" />}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalCount > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground whitespace-nowrap">
            Showing {startIndex} to {endIndex} of {totalCount} entries
          </div>

          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => currentPage > 0 && setCurrentPage(currentPage - 1)}
                  className={currentPage === 0 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>

              {getPageNumbers().map((pageNum) => (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => setCurrentPage(pageNum - 1)}
                    isActive={displayPage === pageNum}
                    className={displayPage === pageNum
                      ? "bg-purple-600 text-white hover:bg-purple-700 cursor-pointer"
                      : "hover:bg-purple-100 cursor-pointer"
                    }
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              ))}

              <PaginationItem>
                <PaginationNext
                  onClick={() => currentPage < totalPages - 1 && setCurrentPage(currentPage + 1)}
                  className={currentPage >= totalPages - 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Clinic User Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this clinic user group "{itemToDelete?.groupName}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDeleteConfirm();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <OperationsHistoryModal
        open={isHistoryModalOpen}
        onOpenChange={(open) => {
          setIsHistoryModalOpen(open);
          if (!open) setSelectedGroupForHistory(null);
        }}
        entityName={
          selectedGroupForHistory
            ? clinicUserGroups.find(g => g.clinicUserGroupId === selectedGroupForHistory)?.groupName || ""
            : ""
        }
        entityType="Clinic User Group"
        auditLogs={clinicUserGroupAuditLogs}
        loading={auditLogsLoading}
      />
    </div>
  );
};
